import React, { useState, useEffect } from 'react';
import { API_URL } from '../config/api';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { MapPin, Loader2, ArrowLeft, Check, X, Square } from 'lucide-react';



export default function Terrains() {
  const { siteId } = useParams();
  const navigate = useNavigate();
  const { user, token, isAgentOrAdmin } = useAuth();
  const [site, setSite] = useState(null);
  const [terrains, setTerrains] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [allReservations, setAllReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTerrain, setSelectedTerrain] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [reservationsDialogOpen, setReservationsDialogOpen] = useState(false);
  const [reserving, setReserving] = useState(false);

  const fetchData = async () => {
    try {
      const [siteRes, terrainsRes] = await Promise.all([
        fetch(`${API_URL}/api/sites/${siteId}`),
        fetch(`${API_URL}/api/terrains?site_id=${siteId}`)
      ]);
      
      const siteData = await siteRes.json();
      const terrainsData = await terrainsRes.json();
      
      setSite(siteData);
      setTerrains(terrainsData);

      if (user) {
        const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
        const resRes = await fetch(`${API_URL}/api/reservations`, {
          headers,
          credentials: 'include'
        });
        if (resRes.ok) {
          const resData = await resRes.json();
          setReservations(resData.filter(r => r.client_id === user.user_id));
          if (isAgentOrAdmin()) {
            setAllReservations(resData);
          }
        }
      }
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [siteId, user, token]);

  const handleReserve = (terrain) => {
    if (!user) {
      navigate('/auth', { state: { from: location } });
      return;
    }
    setSelectedTerrain(terrain);
    setDialogOpen(true);
  };

  const confirmReservation = async () => {
    setReserving(true);
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      
      const response = await fetch(`${API_URL}/api/reservations`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({ terrain_id: selectedTerrain.terrain_id })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail);
      }

      toast.success('Demande de réservation envoyée');
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setReserving(false);
    }
  };

  const handleValidateReservation = async (reservationId, action) => {
    try {
      const headers = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      
      const response = await fetch(`${API_URL}/api/reservations/${reservationId}/${action}`, {
        method: 'PATCH',
        headers,
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Erreur');
      
      toast.success(action === 'validate' ? 'Réservation validée' : 'Réservation rejetée');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const getTerrainStatus = (terrain) => {
    if (terrain.vendu) return { label: 'Vendu', color: 'bg-red-100 text-red-800' };
    if (terrain.reserve) return { label: 'Réservé', color: 'bg-yellow-100 text-yellow-800' };
    return { label: 'Disponible', color: 'bg-green-100 text-green-800' };
  };

  const hasUserReservation = (terrain) => {
    return reservations.some(r => r.terrain_id === terrain.terrain_id && !r.valide);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-4" data-testid="terrains-page">
      <div className="max-w-7xl mx-auto">
        {/* Back button */}
        <Button variant="ghost" onClick={() => navigate(`/sites/${siteId}`)} className="mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour au site
        </Button>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold font-serif mb-2">Terrains - {site?.nom || site?.ville}</h1>
          <p className="text-muted-foreground">{site?.description}</p>
        </div>

        {/* Agent Actions */}
        {isAgentOrAdmin() && (
          <div className="mb-6">
            <Button 
              variant="outline" 
              onClick={() => setReservationsDialogOpen(true)}
              data-testid="view-reservations-btn"
            >
              Voir toutes les réservations ({allReservations.filter(r => terrains.some(t => t.terrain_id === r.terrain_id)).length})
            </Button>
          </div>
        )}

        {/* Terrains Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {terrains.map(terrain => {
            const status = getTerrainStatus(terrain);
            const hasReservation = hasUserReservation(terrain);
            const canReserve = !terrain.reserve && !terrain.vendu && !hasReservation;

            return (
              <Card key={terrain.terrain_id} className="card-hover" data-testid={`terrain-card-${terrain.terrain_id}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <CardTitle className="text-lg">{terrain.titre}</CardTitle>
                    <Badge className={status.color}>{status.label}</Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <MapPin className="w-4 h-4 text-primary" />
                    <span>Lot {terrain.numero_lotissement}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Square className="w-4 h-4 text-primary" />
                    <span>{terrain.surface} m²</span>
                  </div>
                  <div className="pt-3 border-t">
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Prix</span>
                      <span className="text-xl font-bold text-primary">{terrain.prix?.toLocaleString()} FCFA</span>
                    </div>
                  </div>
                </CardContent>
                <CardFooter className="pt-0">
                  <Button 
                    className="w-full btn-pill"
                    disabled={!canReserve}
                    onClick={() => handleReserve(terrain)}
                    data-testid={`reserve-btn-${terrain.terrain_id}`}
                  >
                    {hasReservation ? 'Réservation en attente' : terrain.reserve ? 'Déjà réservé' : 'Réserver'}
                  </Button>
                </CardFooter>
              </Card>
            );
          })}
        </div>

        {terrains.length === 0 && (
          <div className="text-center py-12">
            <MapPin className="w-16 h-16 mx-auto text-muted-foreground/50 mb-4" />
            <p className="text-muted-foreground">Aucun terrain disponible</p>
          </div>
        )}

        {/* Reservation Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Confirmer la réservation</DialogTitle>
              <DialogDescription>
                Vous allez demander à réserver le terrain "{selectedTerrain?.titre}"
              </DialogDescription>
            </DialogHeader>
            {selectedTerrain && (
              <div className="space-y-3 py-4">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Surface</span>
                  <span className="font-semibold">{selectedTerrain.surface} m²</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Prix</span>
                  <span className="font-semibold text-primary">{selectedTerrain.prix?.toLocaleString()} FCFA</span>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
              <Button onClick={confirmReservation} disabled={reserving}>
                {reserving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Confirmer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reservations Dialog (Agent/Admin) */}
        <Dialog open={reservationsDialogOpen} onOpenChange={setReservationsDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Réservations - {site?.nom || site?.ville}</DialogTitle>
            </DialogHeader>
            <div className="max-h-96 overflow-y-auto">
              {allReservations
                .filter(r => terrains.some(t => t.terrain_id === r.terrain_id))
                .map(reservation => {
                  const terrain = terrains.find(t => t.terrain_id === reservation.terrain_id);
                  return (
                    <div 
                      key={reservation.reservation_id}
                      className="flex items-center justify-between p-3 border-b last:border-0"
                    >
                      <div>
                        <p className="font-medium">{terrain?.titre}</p>
                        <p className="text-sm text-muted-foreground">{reservation.client_nom}</p>
                        <p className="text-xs text-muted-foreground">{reservation.client_email}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {reservation.valide ? (
                          <Badge className="bg-green-100 text-green-800">Validée</Badge>
                        ) : (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-green-600"
                              onClick={() => handleValidateReservation(reservation.reservation_id, 'validate')}
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600"
                              onClick={() => handleValidateReservation(reservation.reservation_id, 'reject')}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              {allReservations.filter(r => terrains.some(t => t.terrain_id === r.terrain_id)).length === 0 && (
                <p className="text-center text-muted-foreground py-8">Aucune réservation</p>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
